// dependencies
import React, { lazy, useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Disclaimers, PricePoint, defineMessages, TopLegals, LanguageSwitcher } from 'ouisys-component-library';
import * as RDS from 'ouisys-engine/dist/common-types/RemoteDataState';
import strategy from 'ouisys-engine/dist/flows/strategy';
import { identifyStrategy, StrategyFlowActionMaps } from 'ouisys-engine/dist/reducers/strategy';
import {
  IDENTIFYMSISDNEntryFailure,
  IDENTIFYMSISDNEntrySuccess,
  IIdenyifyFlowByMsisdnSumbitNextData
} from 'ouisys-engine/dist/reducers/strategy/StrategyTypes';
import store from 'ouisys-engine/dist/store';
import { IApplicationState } from 'ouisys-engine/dist/store/reducers';
import { setOnLoadUtilities } from 'ouisys-engine/dist/utilities';
import { getConfig } from 'ouisys-phone-input/dist/common/PhoneInput';
import { tracker } from 'ouisys-engine/dist/reducers/strategy';

// local
import Loader from './components/Loader';
import OperatorSelection from './components/OperatorSelection';
import PhoneNumberEntryStep from './components/PhoneNumberEntryStep';
import SwitchLang from './components/SwitchLang';
import PaymentStatus from './components/PaymentStatus';
import { legals, Translate, TranslationProvider } from './localization/index';
import searchToObject from './utils/searchToObject';
import PreLander from './components/PreLander';
import Click2smsFlow from './flows/Click2smsFlow';
import MoFlow from './flows/MoFlow';
import MoRedirFlow from './flows/MoRedirFlow';
import OneClickFlow from './flows/OneClickFlow';
import PinFlow from './flows/PinFlow';
import UssdFlow from './flows/UssdFlow';
import CreditCardFlow from './flows/CreditCardFlow';
import './Bloxz.scss';

// error messages messages which might come from backend
const defaultMessages = defineMessages({
  InvalidMSISDN: {
    id: 'InvalidMSISDN',
    defaultMessage: 'This mobile number is incorrect, try again'
  },
  AlreadySubscribed: {
    id: 'AlreadySubscribed',
    defaultMessage: 'You are already a subscriber!'
  },
  UnknownError: {
    id: 'UnknownError',
    defaultMessage: 'An unknown error occurred.'
  },
  InvalidPIN: {
    id: 'InvalidPIN',
    defaultMessage: 'Please enter the correct PIN that you received on your phone'
  },
  SessionBlocked: {
    id: 'SessionBlocked',
    defaultMessage: 'Session Blocked!'
  },
  TooEarly: {
    id: 'TooEarly',
    defaultMessage: 'You must first submit a mobile number'
  },
  didnt_receive_pin_yet: {
    id: 'didnt_receive_pin_yet',
    defaultMessage: "Didn't receive PIN yet?"
  }
});

interface HOCProps {}

const PRIMARY_LOCALE = 'en';
const PRIMARY_LOCALE_TEXT = 'English';

const Root = ({
}: HOCProps) => {
  const [locale, setLocale] = useState(PRIMARY_LOCALE);
  const [operator, setOperator] = useState<string | null>(null);
  const [msisdn, setMsisdn] = useState<string>(getConfig(process.env.country).commonPrefix);
  const [skipPrelander, setPrelander] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(false);
  const dispatch = useDispatch();
  const { currentState } = useSelector((state: IApplicationState) => state.strategy);
  const urlParams = searchToObject();
  const [isModal, setModal] = useState(false);
  const [isOverlay, setOverlay] = useState(false)
  const { d_theme, d_locale } = urlParams as { d_theme: string; d_locale: string; };
  
  
  //image library
  const logo = require("./assets/imgs/logo.png");
  const globe = require("./assets/imgs/globe.svg");
  const vod = require("./assets/imgs/vod-devices.png");
  const movies = require("./assets/imgs/movies.svg");
  const news = require("./assets/imgs/news.svg");
  const lifestyle = require("./assets/imgs/lifestyle.svg");
  const sports = require("./assets/imgs/sports.svg");
  const entertainment2 = require("./assets/imgs/entertainment.jpg");
  const news2 = require("./assets/imgs/news.jpg");
  const lifestyle2 = require("./assets/imgs/lifestyle.jpg");
  const sports2 = require("./assets/imgs/sports.jpg");
  const mobile = require("./assets/imgs/mobile.svg");

  // show or hide pre-lander
  const checkSkipPrelander = () => {
    if (urlParams['skip-prelander']) {
      setPrelander(urlParams['skip-prelander'] === 'true');
    }
  };

  // Show or hide thank you
  const checkPaymentStatus = () => {
    if (urlParams['payment-status']) {
      setPaymentStatus(urlParams['payment-status'] === 'true');
    }
  };


  const switchLang = (lang: string) => {
    setLocale(lang);
    if (typeof window !== 'undefined') {
      window.languageCode = lang;
      document.getElementsByTagName('html')[0].setAttribute('lang', lang);
    }
  };

  const divFocus = () => {
    // show the overlay
    setOverlay(true);
    if (typeof window !== 'undefined') {
      // reset bx-holder class
      if (document?.querySelector('.bx-holder'))
        document.querySelector('.bx-holder').className = 'bx-holder';
      // add share-horizontal on divFocus
      if (document?.querySelector('#divFocus'))
        document.querySelector('#divFocus').className = 'shake-horizontal';
    }
  };


  const [isActive, setIsActive] = useState(false);

  const handleClick = () =>  {
    setIsActive(true)
    document.body.style.overflowY = 'hidden';
    document.body.style.overflowX = 'auto';
  };

  const identifyFlowByMsisdnInterface = (
    rds: RDS.RemoteDataState<IDENTIFYMSISDNEntryFailure, IDENTIFYMSISDNEntrySuccess>,
    nextData: IIdenyifyFlowByMsisdnSumbitNextData
  ) => {
    const { nextAction } = nextData;
    return (
      <PhoneNumberEntryStep
        msisdn={msisdn}
        rds={rds}
        onEnd={(msisdn) => {
          setMsisdn(msisdn);
          dispatch(StrategyFlowActionMaps[nextAction](msisdn));
        }}
        locale={locale}
      />
    );
  };

  useEffect(() => {
    dispatch(identifyStrategy());
    checkSkipPrelander();
    checkPaymentStatus();
    

    if (typeof window != "undefined") {
      window.languageCode = locale;
      document.getElementsByTagName('html')[0].setAttribute("lang", locale)
    }
    //Set locale based on url designer para
    if (d_locale) {
      switchLang(d_locale)
    }

    if (typeof window != "undefined") {
      if (/^es\b/.test(window.navigator.language)) {
        setLocale("es")
        tracker.customEvent("Lang-Switcher", "autodetect", "browserLocale", "es")
      }
  
      if (/^fr\b/.test(window.navigator.language)) {
        setLocale("fr")
        tracker.customEvent("Lang-Switcher", "autodetect", "browserLocale", "fr")
      }
  
      if (/^en\b/.test(window.navigator.language)) {
        setLocale("en")
        tracker.customEvent("Lang-Switcher", "autodetect", "browserLocale", "en")
      }
  
    }
    
    setOnLoadUtilities((currentState as any)?.result?.config);

    
  }, []);

  return (
    <TranslationProvider locale={locale}>
      <div className={`bx-${locale} theme-${d_theme === 'dark' ? 'dark' : 'light'}`} >
        <div className={`bx-container bx-modal-${isModal} bx-overlay-${isOverlay}`}>

          {/* pre-lander */}
          {!skipPrelander && <PreLander onClick={divFocus} />}

          {/* Payment Status */}
          {
            paymentStatus &&
            <PaymentStatus  />
          }

          {/* default-lander */}
          <div className={`creativeArea ${paymentStatus == true ? "hide" : ""}`} onClick={divFocus}>
            <header>
              <div className="container">
                <div className="logo">
                  <img src={logo} />
                </div>
                <p><Translate id="slogan" defaultMessage="Get Endless Entertainment, News, Sports and Premium Content" /></p>
                <div>
                  <SwitchLang locale={locale} switchLang={switchLang} />
                </div>
              </div>
              <div className="features">
                <div className="container">
                  <div><span className="material-icons">gpp_good</span><span>
                    <div><Translate id="secureConnection" defaultMessage="Secure Connection" /></div>
                  </span></div>
                  <div><span className="material-icons">workspace_premium</span><span>
                    <div><Translate id="premiumContent" defaultMessage="Premium Content" /></div>
                  </span></div>
                  <div><span className="material-icons">devices</span><span>
                    <div><Translate id="availableEverywhere" defaultMessage="Available Everywhere" /></div>
                  </span></div>
                </div>
              </div>
            </header>

            <main>
              <section className="hero">
                <div className="container">
                  <div className="title-wrap"><img src={globe} />
                    <h2>
                      <div key="online" className=""><span><Translate id="online" defaultMessage="Online" /> <br /><strong><Translate id="activation" defaultMessage="Activation" /></strong></span>
                      </div>
                    </h2>
                  </div>
                  <div className="title-wrap mobile"><img src={mobile} />
                    <h2>
                      <div key="mobile" className=""><span><Translate id="mobile" defaultMessage="Mobile" /> <strong><Translate id="activation" defaultMessage="Activation" /></strong></span>
                      </div>
                    </h2>
                  </div>
                  <div className="btn-wrap">
                    <button type="button" id="continueBtn" className="hide-desktop" onClick={() => {handleClick(); tracker.advancedInPreFlow("step1");}}><Translate id="continue" defaultMessage="Continue" /></button>
                    <p><Translate id="connectionFast" defaultMessage="Your Connection : Fast"></Translate></p>
                  </div>
                  <p><Translate id="slogan" defaultMessage="Get Endless Entertainment, News, Sports and Premium Content" /></p>
                </div>
              </section>
              <section className="icons-wrap">
                <div><img src={movies} />
                  <h5><Translate id="entertainment" defaultMessage="Entertainment" /></h5>
                </div>
                <div><img src={lifestyle} />
                  <h5><Translate id="lifestyle" defaultMessage="Lifestyle" /></h5>
                </div>
                <div><img src={news} />
                  <h5><Translate id="news" defaultMessage="News" /></h5>
                </div>
                <div><img src={sports} />
                  <h5><Translate id="sports" defaultMessage="Sports" /></h5>
                </div>
              </section>
              <section className="steps-wrap">
                <h4><Translate id="steps" defaultMessage="Get access in 3 easy steps" /></h4>
                <div className="container">
                  <div><Translate id="step1" defaultMessage="Create your account" /></div>
                  <div><Translate id="step2" defaultMessage="Verify your account" /></div>
                  <div><Translate id="step3" defaultMessage="Access your content" /></div>
                </div>
              </section>
              <section className="find-wrap">
                <h3><Translate id="findOutWhy" defaultMessage="Find out why thousands of people are switching to us" /></h3>
                <div className="container">
                  <div>
                    <figure>
                      <img src={vod} />
                    </figure>
                  </div>
                  <div>
                    <ul>
                      <li><Translate id="why1" defaultMessage="Secure Platform" /></li>
                      <li><Translate id="why2" defaultMessage="Premium Unlimited Content" /></li>
                      <li><Translate id="why3" defaultMessage="Multi Devices" /></li>
                      <li><Translate id="why4" defaultMessage="Ad-Free" /></li>
                    </ul>
                  </div>
                </div>
                <div className="container">
                  <Translate id="whyP" defaultMessage="Experience an endless supply of thrilling and engaging online content right at your fingertips. Our secure content platform offers you a wide range of video contents, providing the ultimate all-in-one destination for premium entertainment with unparalleled convenience. Just launch Online Activation on your device, select your content, and be immersed in endless excitement." />
                </div>
              </section>
              <div>
                <div className="container">
                  <div className="btn-wrap">
                    <button type="button" id="continueBtn2" className="hide-desktop" onClick={() => {handleClick(); tracker.advancedInPreFlow("step1");}}><Translate id="continue" defaultMessage="Continue" /></button>
                    <p><Translate id="connectionFast" defaultMessage="Your Connection : Fast"></Translate></p>
                  </div>
                </div>
              </div>
              <section className="thumbnails-wrap">
                <div className="container">
                  <div>
                    <h6><Translate id="entertainment" defaultMessage="Entertainment" /></h6>
                    <figure>
                      <img src={entertainment2} />
                    </figure>
                  </div>
                  <div>
                    <h6><Translate id="lifestyle" defaultMessage="Lifestyle" /></h6>
                    <figure>
                      <img src={lifestyle2} />
                    </figure>
                  </div>
                  <div>
                    <h6><Translate id="news" defaultMessage="News" /></h6>
                    <figure>
                      <img src={news2} />
                    </figure>
                  </div>
                  <div>
                    <h6><Translate id="sports" defaultMessage="Sports" /></h6>
                    <figure>
                      <img src={sports2} />
                    </figure>
                  </div>
                </div>
              </section>
            </main>
            <footer>
              <div className="container">
                <div className="disclaimer">
                  <a className="each" href="/content/en/privacy-movio/?country=es&service=buz2mobile" target="_blank">
                    <Translate id="privacy-policy" defaultMessage="Privacy Policy"></Translate>
                  </a>
                  <a className="each" href="/content/en/refund-policy-movio/?country=es&service=buz2mobile" target="_blank">
                    <Translate id="refund-policy" defaultMessage="Refund Policy"></Translate>
                  </a>
                  <a className="each" href="/content/en/terms-movio/?country=es&service=buz2mobile" target="_blank">
                    <Translate id="terms-conditions" defaultMessage="Terms & Conditions"></Translate>
                  </a>
                  <a className="each" href="/content/en/faq-movio/?country=es&service=buz2mobile" target="_blank">
                    <Translate id="faq" defaultMessage="FAQ"></Translate>
                  </a>
                </div>
              </div>
            </footer>
          </div>

          <div
            className={`bx-holder with-prelander-${skipPrelander} ${ isActive ? "is-visible" : ""}`}
            onClick={(ev) => ev.stopPropagation()}
          >

            {process.env.country == "qa" && (
              <PricePoint className="price-point" locale={locale} legals={legals} />
            )}

            {strategy({
              identifyFlowByMsisdn: (rds, nextData) =>
                identifyFlowByMsisdnInterface(rds, nextData),
              identifyFlowByOperatorSelect: (data) => (
                <OperatorSelection
                  setOperator={setOperator}
                  {...data}
                />
              ),
              pinFlow: (nextData) => (
                <PinFlow
                  nextData={nextData}
                  locale={locale}
                  msisdn={msisdn}
                  operator={operator}
                  setMsisdn={setMsisdn}
                />
              ),
              moFlow: (nextData) => (
                <MoFlow
                  nextData={nextData}
                  locale={locale}
                  msisdn={msisdn}
                  operator={operator}
                  setMsisdn={setMsisdn}
                />
              ),
              moRedirFlow: (nextData) => (
                <MoRedirFlow
                  nextData={nextData}
                  locale={locale}
                  msisdn={msisdn}
                  operator={operator}
                  setMsisdn={setMsisdn}
                />
              ),
              oneClickFlow: (nextData) => (
                <OneClickFlow
                  nextData={nextData}
                  locale={locale}
                  operator={operator}
                />
              ),
              click2smsFlow: (nextData) => (
                <Click2smsFlow
                  nextData={nextData}
                  locale={locale}
                  operator={operator}
                />
              ),
              ussdFlow: (nextData) => (
                <UssdFlow
                  nextData={nextData}
                  locale={locale}
                  msisdn={msisdn}
                  operator={operator}
                  setMsisdn={setMsisdn}
                />
              ),
              creditCardFlow: (nextData) =>
                <CreditCardFlow
                  nextData={nextData}
                  locale={locale}
                />
            })(currentState)}

          </div>

          {RDS.WhenLoading(null, () => (
            <div className="loading-wrapper">
              <Loader />
              <div>
                <Translate id="loadingLabel" defaultMessage="Please wait..." />
              </div>
            </div>
          ))(currentState?.result as RDS.RemoteDataState<unknown, unknown>)}

        </div>

        <div className="legalArea">
          {process.env.country !== 'qa' && (
            <PricePoint className="price-point" locale={locale} legals={legals} />
          )}
          <Disclaimers className="disclaimer" locale={locale} legals={legals} flow="pin" />
        </div>

      </div>
    </TranslationProvider>
  );
};

const R = () => (
  <Provider store={store}>
    <Root />
  </Provider>
);

export default R;
